import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  FormGroup,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { ChangeEvent, useContext, useEffect, useState } from 'react';
import { StockCountZonesContext, StockCountZonesContextValues } from '../../contexts/StockCountZonesContext';
import { UserContext } from '../../../../components/shared/useUser';
import useNswagClient from '../../../../hooks/api/useNswagClient';
import useLogError from '../../../../hooks/useLogError';
import { PrimaryCategoryModel } from '../../../../app/services/api/generated';

const StockCountZonesCreateCategoriesModal = () => {
  //Context
  const {
    t,
    isCategoriesModalOpen,
    handleClose,
    categoryData,
    handleCategories,
    firstSelectedStore,
  } = useContext(StockCountZonesContext) as StockCountZonesContextValues;

  const { selectedStore } = useContext(UserContext);
  //API hooks
  const { getCategories } = useNswagClient();
  const { logError } = useLogError();

  //Local state
  const [dataFromApi, setDataFromApi] = useState<PrimaryCategoryModel[] | undefined>([]);
  const [tempCategoryData, setTempCategoryData] = useState<PrimaryCategoryModel[] | undefined>([]);
  const [isChecked, setIsChecked] = useState(false);

  //Lifecycle
  useEffect(() => {
    if (isCategoriesModalOpen && firstSelectedStore?.storeNumber) {
      getData();
    } else if (!isCategoriesModalOpen) {
      setDataFromApi([]);
    }
  }, [isCategoriesModalOpen, firstSelectedStore?.storeNumber]);

  //Handlers
  const getData = () => {
    const storeNumber = firstSelectedStore?.storeNumber ?? selectedStore?.storeNumber ?? '';
    if (!storeNumber) {
      setDataFromApi([]);
      return;
    }
    getCategories(storeNumber)
      .then((result) => {
        if (result) {
          setDataFromApi(
            result.data?.sort((a, b) =>
              a.primaryCategoryName?.localeCompare(b.primaryCategoryName ?? '') ?? 0,
            ),
          );
        } else {
          logError(result);
        }
      })
      .catch((error) => {
        logError(error);
      });
  };

  const handleCategoryCheckboxChange = (category: string, isChecked: boolean) => {
    if (isChecked) {
      const exist = tempCategoryData?.find(row => row.primaryCategoryName === category);
      if (!exist) {
        const selectedCategory = dataFromApi?.find(row => row.primaryCategoryName === category);
        if (selectedCategory) {
          setTempCategoryData(prev => [...(prev ?? []), selectedCategory]);
        }
      }
    } else {
      setIsChecked(false);
      setTempCategoryData(prevData =>
        prevData?.filter(row => row.primaryCategoryName !== category) || [],
      );
    }
  };

  const handleSelectAllChange = (event: ChangeEvent<HTMLInputElement>) => {
    const checked = event.target.checked;
    if (checked) {
      setTempCategoryData(dataFromApi);
    } else {
      setTempCategoryData([]);
    }
  };


  const handleConfirm = () => {
    handleCategories(tempCategoryData || []);
    setTempCategoryData([]);
  };

  const close = () => {
    setTempCategoryData([]);
    handleClose();
  };

  return (
    <Dialog open={isCategoriesModalOpen}
      onClose={close}
      maxWidth="sm"
      fullWidth>
      <DialogTitle>
        {t('categories.title')}
        <IconButton aria-label={t('modals.close')}
          onClick={close}
          sx={{
            position: 'absolute',
            right: 16,
            top: 8,
            color: (theme) => theme.palette.custom.gray[800],
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        <TableContainer
          sx={{ overflowY: 'auto', border: '1px solid' }}
          style={{ maxHeight: '400px' }}
        >
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>{t('categories.name')}</TableCell>
                <TableCell>{t('items.title')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {dataFromApi?.map((row) => (
                <TableRow key={row.id}>
                  <TableCell style={{ display: 'flex', alignItems: 'center' }}>
                    <Checkbox
                      color="primary"
                      inputProps={{
                        'aria-label': `select category ${row.primaryCategoryName}`,
                      }}
                      checked={isChecked || tempCategoryData?.some((item) => item.primaryCategoryName === row.primaryCategoryName) || categoryData?.some((item) => item.primaryCategoryName === row.primaryCategoryName)}
                      onChange={(e) =>
                        handleCategoryCheckboxChange(row.primaryCategoryName!, e.target.checked)
                      }
                    />
                    {row.primaryCategoryName}
                  </TableCell>
                  <TableCell>{row.itemCount}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        <FormGroup sx={{ pt: '10px' }}>
          <FormControlLabel
            control={<Checkbox onChange={handleSelectAllChange} />}
            label={t('categories.addAll', 'Add all categories')}
          />
        </FormGroup>
      </DialogContent>

      <DialogActions>
        <Button variant="secondary"
          size="lg"
          onClick={close}>
          {t('modals.cancel')}
        </Button>
        <Button size="lg"
          onClick={handleConfirm}
          disabled={!tempCategoryData || tempCategoryData.length === 0}>
          {t('modals.confirm')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default StockCountZonesCreateCategoriesModal;